import {Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {OrganizationController} from '../../../api/organization/organization.controller';
import {StatusCallAccountController} from '../../../api/status-calls/status-call-account.controller';
import {OrganizationData} from '../../../model/organization/organization-data';

type Props = {
  open: boolean
  onClose: () => void
  onSuccess: () => Promise<void>
  translateLocal: (key: string, params?: Record<string, unknown>) => string
};

export const AddPoolHoursModal = ({open, onClose, onSuccess, translateLocal}: Props) => {
  const [organizations, setOrganizations] = useState<OrganizationData[]>([]);
  const [organization, setOrganization] = useState<OrganizationData | null>(null);
  const [hours, setHours] = useState<string>('1.0');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      OrganizationController.fetchOrganizations().then(setOrganizations);
      setOrganization(null);
      setHours('1.0');
      setComment('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!organization) return;
    const hoursNum = Number(hours);
    if (Number.isNaN(hoursNum) || hoursNum <= 0) {
      setError('Invalid hours');
      return;
    }
    setSubmitting(true);
    try {
      await StatusCallAccountController.grantStatusCallHours(organization.id, {
        hours: hoursNum,
        comment: comment || null,
      });
      await onSuccess();
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translateLocal('modal.add-hours.title')}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Autocomplete
            options={organizations}
            value={organization}
            onChange={(_, v) => setOrganization(v)}
            getOptionLabel={(o) => o.name}
            renderInput={(params) => <TextField {...params} label={translateLocal('modal.schedule.organization')}/>}
          />
          <TextField
            label={translateLocal('modal.add-hours.hours')}
            type="number"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            inputProps={{step: 0.5, min: 0.5}}
          />
          <TextField
            label={translateLocal('modal.add-hours.comment')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            rows={2}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translateLocal('modal.schedule.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!organization || submitting}
        >
          {translateLocal('modal.add-hours.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
